/* app/providers/AuthProvider.jsx — РЕАЛЬНЫЙ AuthContext (боевая сессия Django).
   Контракт значения сохранён: {user, workspaces, activeWorkspace, switchWorkspace, login, logout, refreshUser}
   (+ loading). Bootstrap на маунте: ensureCsrf → /api/auth/me → /api/auth/workspaces.
   Форма данных под потребителей (Sidebar/WorkspaceSwitcher/UserMenu):
   - user.initials — derived из full_name (бэк не присылает);
   - workspace.subscription {plan, planLabel, expires_at, ...} ← бэк {plan_slug, plan_name, ...};
   - workspace.usage — НЕ из /workspaces/ (живёт в /api/subscription/me/); опускаем (UI деградирует на email). */

import React from 'react';
import { ensureCsrf, setActiveWorkspaceId } from '../../lib/api/client.js';
import * as authApi from '../../lib/api/auth.js';
import { FullScreenSpinner } from '../../components/ui/Spinner.jsx';
import { isDemoRoute } from '../../screens/demo/demoAccess.jsx';

  const { createContext, useContext, useState, useCallback, useMemo, useEffect } = React;

  const AuthContext = createContext(null);

  function deriveInitials(fullName, email) {
    const src = (fullName || '').trim();
    if (src) {
      const parts = src.split(/\s+/).filter(Boolean);
      const a = parts[0] ? parts[0][0] : '';
      const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (a + b).toUpperCase();
    }
    return (email || '?').slice(0, 1).toUpperCase();
  }

  function normalizeUser(data) {
    if (!data) return null;
    return { ...data, initials: data.initials || deriveInitials(data.full_name, data.email) };
  }

  // Бэк: subscription {plan_slug, plan_name, is_paid, expires_at, is_effectively_active, expires_soon}|null
  // UI:  subscription {plan, planLabel, expires_at, ...}. membership совпадает (is_owner/is_admin/role_name).
  function normalizeWorkspace(w) {
    const s = w.subscription;
    const subscription = s
      ? {
          plan: s.plan_slug,
          planLabel: s.plan_name,
          expires_at: s.expires_at,
          is_paid: s.is_paid,
          expires_soon: s.expires_soon,
          is_effectively_active: s.is_effectively_active,
        }
      : null;
    // ...w несёт и `sections` ({analytics, integrations} — тарифные флаги разделов): не
    // пересобирать объект по полям, иначе флаги потеряются и разделы «включатся» молча.
    return { ...w, subscription }; // usage опущен (приходит из /api/subscription/me/, см. реестр дыр)
  }

  // Активное пространство = запись списка по activeId (иначе первая, иначе null). Единственный
  // источник, из которого меню/таб-бар/гард /analytics/палитра/уведомления читают тарифные
  // флаги разделов (workspace.sections): смена activeId и обновление списка меняют результат.
  function pickActiveWorkspace(workspaces, activeId) {
    const list = workspaces || [];
    return list.find((w) => w.id === activeId) || list[0] || null;
  }

  function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadWorkspaces = useCallback(async () => {
      const r = await authApi.listWorkspaces();
      if (r.ok && r.data) {
        const list = (r.data.workspaces || []).map(normalizeWorkspace);
        setWorkspaces(list);
        const aid = r.data.active_id != null ? r.data.active_id : (list[0] && list[0].id) || null;
        setActiveId(aid);
        setActiveWorkspaceId(aid); // → apiFetch начнёт слать X-Workspace-Id
        return list;
      }
      return [];
    }, []);

    // Bootstrap сессии: csrf → me → workspaces. Пока в полёте — FullScreenSpinner
    // (иначе RequireAuth редиректнёт на /signin до ответа /me).
    useEffect(() => {
      let alive = true;
      // Публичный демо-стенд (/business-demo) — гость без сессии: не дёргаем csrf/me/workspaces
      // (они всё равно 401 и не нужны на no-login странице).
      if (isDemoRoute()) { setUser(null); setLoading(false); return () => { alive = false; }; }
      (async () => {
        await ensureCsrf();
        const r = await authApi.me();
        if (!alive) return;
        if (r.ok && r.data) {
          setUser(normalizeUser(r.data));
          await loadWorkspaces();
        } else {
          setUser(null); // 401/403 → гость → RequireAuth уведёт на /signin
        }
        if (alive) setLoading(false);
      })();
      return () => { alive = false; };
    }, [loadWorkspaces]);

    const activeWorkspace = useMemo(
      () => pickActiveWorkspace(workspaces, activeId),
      [workspaces, activeId]
    );

    // Реальное переключение: сервер-сессия (switch) + localStorage (X-Workspace-Id) — два независимых
    // слоя, оба кормят resolve_active_workspace; держим в синхроне. Затем обновляем флаги/подписку.
    const switchWorkspace = useCallback(async (id) => {
      const r = await authApi.switchWorkspace(id);
      if (r.ok) {
        setActiveWorkspaceId(id);
        setActiveId(id);
        loadWorkspaces();
      }
      return r;
    }, [loadWorkspaces]);

    // login(userData): принимает payload пользователя (как раньше mock) — экран SignIn сам зовёт
    // authApi.login и передаёт сюда res.data. Поднимаем сессию пространств.
    const login = useCallback(async (userData) => {
      if (userData) setUser(normalizeUser(userData));
      await loadWorkspaces();
    }, [loadWorkspaces]);

    const logout = useCallback(async () => {
      await authApi.logout();
      setUser(null);
      setWorkspaces([]);
      setActiveId(null);
      setActiveWorkspaceId(null);
    }, []);

    const refreshUser = useCallback(async () => {
      const r = await authApi.me();
      if (r.ok && r.data) setUser(normalizeUser(r.data));
      return r;
    }, []);

    const value = useMemo(() => ({
      user, workspaces, activeWorkspace, loading,
      switchWorkspace, login, logout, refreshUser, refreshWorkspaces: loadWorkspaces,
    }), [user, workspaces, activeWorkspace, loading, switchWorkspace, login, logout, refreshUser, loadWorkspaces]);

    if (loading) return <FullScreenSpinner label="Загрузка…" />;

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  const useAuth = () => useContext(AuthContext);

export { AuthProvider };
export { useAuth };
export { normalizeWorkspace, pickActiveWorkspace };
